// import { Plus } from 'lucide-react';
// import { useMutation } from '@platform/api-client';
import { Button, /* ChoiceChips, */ formatNumber, PageHeader, SearchBox } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { useState } from 'react';
import {
  asTree,
  countsWithDescendants,
  matchingNodes,
  parentIdsOf,
  unfoldedNodes,
  type Category,
  type CategoryStatus,
  // type CreateCategoryBody,
} from './categories.ts';
import { CategoryCoverageCard } from './CategoryCoverageCard.tsx';
import { CategoryDetails } from './CategoryDetails.tsx';
// import { CategorySheet } from './CategorySheet.tsx';
import { ResourceTable } from '../ResourceTable.tsx';
import { usePageContext } from '../usePageContext.ts';
import { useCategories, useCategoryProductCounts, useTenantDetail } from './useCatalogQueries.ts';
import { useCategoryColumns } from './useCategoryColumns.tsx';
import styles from './Categories.module.css';

// const CATEGORY_VIEWS: readonly CategoryStatus[] = ['active', 'archived'];
const SUMMARY_SEPARATOR = ' · ';

type Props = {
  api: TypedClient;
  status: CategoryStatus;
  onStatusChange: (status: CategoryStatus) => void;
};

export const CategoriesPage = ({ api, status /* , onStatusChange */ }: Props) => {
  // const { t, locale, notify, describeError, invalidate } = usePageContext();
  const { t, locale } = usePageContext();
  // const [editing, setEditing] = useState<Category | null>(null);
  // const [sheetOpen, setSheetOpen] = useState(false);
  // const [refusal, setRefusal] = useState<string | null>(null);
  const [viewing, setViewing] = useState<Category | null>(null);
  const [search, setSearch] = useState('');
  const [folded, setFolded] = useState<ReadonlySet<string>>(new Set());

  const active = useCategories(api, 'active');
  const archived = useCategories(api, 'archived');
  const list = status === 'active' ? active : archived;
  const ownCounts = useCategoryProductCounts(api).data;
  const locales = useTenantDetail(api).data?.locales ?? [locale];

  // const save = useMutation({
  // mutationFn: (body: CreateCategoryBody) =>
  // editing
  // ? api.patch('/admin/categories/{id}', {
  // path: { id: editing.id },
  // body: { ...body, version: editing.version },
  // })
  // : api.post('/admin/categories', { body }),
  // onSuccess: async () => {
  // setSheetOpen(false);
  // setEditing(null);
  // notify(t('catalog.categorySaved'));
  // await invalidate('categories');
  // },
  // });

  // const archive = useMutation({
  // mutationFn: (category: Category) =>
  // api.post('/admin/categories/{id}/archive', {
  // path: { id: category.id },
  // body: { version: category.version },
  // }),
  // onSuccess: async () => {
  // setRefusal(null);
  // notify(t('catalog.categoryArchived'));
  // await invalidate('categories');
  // },
  // onError: (error) => {
  // setRefusal(describeError(error) ?? null);
  // },
  // });

  // const openCreate = () => {
  // setEditing(null);
  // setSheetOpen(true);
  // };
  // const openEdit = (category: Category) => {
  // setEditing(category);
  // setSheetOpen(true);
  // };

  const categories = list.data?.items ?? [];
  const knownCategories = [...(active.data?.items ?? []), ...(archived.data?.items ?? [])];
  const parents = parentIdsOf(categories);
  const searching = search.trim() !== '';
  const nodes = asTree(categories);
  const rows = searching ? matchingNodes(nodes, search) : unfoldedNodes(nodes, folded);
  const productCounts = ownCounts === undefined ? undefined : countsWithDescendants(categories, ownCounts);
  const roots = categories.filter((category) => category.parentId === null).length;
  const totalProducts = [...(ownCounts?.values() ?? [])].reduce((total, products) => total + products, 0);
  const summary =
    status === 'active'
      ? [
          t('categoryList.summaryRoots', { count: roots, total: formatNumber(roots, locale) }),
          t('categoryList.summaryChildren', {
            count: categories.length - roots,
            total: formatNumber(categories.length - roots, locale),
          }),
          t('categoryList.summaryProducts', {
            count: totalProducts,
            total: formatNumber(totalProducts, locale),
          }),
        ].join(SUMMARY_SEPARATOR)
      : t('categoryList.summaryArchived', {
          count: categories.length,
          total: formatNumber(categories.length, locale),
        });
  const allFolded = parents.size > 0 && [...parents].every((id) => folded.has(id));
  const columns = useCategoryColumns({
    locales,
    parents,
    folded,
    productCounts,
    // archiving: archive.isPending,
    onToggle: (categoryId) => {
      const next = new Set(folded);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      setFolded(next);
    },
    // onEdit: openEdit,
    // onArchive: (category) => {
    // archive.mutate(category);
    // },
  });

  return (
    <>
      <PageHeader
        title={t('nav.categories')}
        context={t('catalog.categoriesContext')}
        // action={
        //   <Button icon={<Plus size={20} aria-hidden="true" />} onClick={openCreate}>
        //     {t('catalog.newCategory')}
        //   </Button>
        // }
      />
      {/* {refusal === null ? null : (
        <p role="alert" data-testid="row-error" className={styles['refusal']}>
          {refusal}
        </p>
      )} */}
      <div className={styles['layout']}>
        <ResourceTable
          query={list}
          columns={columns}
          rows={rows}
          rowKey={(node) => node.category.id}
          // onRowClick={(node) => {
          // openEdit(node.category);
          // }}
          onRowClick={(node) => {
            setViewing(node.category);
          }}
          toolbar={
            <div className={styles['listTools']}>
              <SearchBox
                label={t('categoryList.search')}
                placeholder={t('categoryList.search')}
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                }}
                onSearch={setSearch}
              />
              {/* <ChoiceChips
                legend={t('catalog.status')}
                hideLegend
                locale={locale}
                value={status}
                choices={CATEGORY_VIEWS.map((value) => ({
                  value,
                  label: t(`categoryList.view_${value}`),
                  count: (value === 'active' ? active : archived).data?.total,
                }))}
                onChange={onStatusChange}
              /> */}
              {parents.size === 0 || searching ? null : (
                <span className={styles['foldAll']}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setFolded(allFolded ? new Set() : new Set(parents));
                    }}
                  >
                    {allFolded ? t('categoryList.unfoldAll') : t('categoryList.foldAll')}
                  </Button>
                </span>
              )}
            </div>
          }
          footer={categories.length === 0 ? undefined : <span className={styles['muted']}>{summary}</span>}
          emptyTitle={searching ? t('categoryList.noMatch') : t('catalog.noCategories')}
          emptyDescription={searching ? t('categoryList.noMatchHelp') : t('catalog.noCategoriesHelp')}
        />
        <CategoryCoverageCard categories={active.data?.items ?? []} locales={locales} />
      </div>
      {viewing === null ? null : (
        <CategoryDetails
          category={viewing}
          parent={knownCategories.find((category) => category.id === viewing.parentId) ?? null}
          products={productCounts?.get(viewing.id)}
          locales={locales}
          onClose={() => {
            setViewing(null);
          }}
        />
      )}
      {/* {sheetOpen ? (
        <CategorySheet
          open={sheetOpen}
          editing={editing}
          parents={categories.filter((category) => category.parentId === null)}
          locales={locales}
          locale={locale}
          submitting={save.isPending}
          errorMessage={save.isError ? (describeError(save.error) ?? null) : null}
          onClose={() => {
            setSheetOpen(false);
            setEditing(null);
            save.reset();
          }}
          onSubmit={(body) => {
            save.mutate(body);
          }}
        />
      ) : null} */}
    </>
  );
};
